import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store';
import { fetchTags, createTag, assignTag } from '../../store/actions/FolderAction';
import Badge from '../shared/TailwindComponents/Badge';
import { BadgeColor } from '../shared/TailwindComponents/types/badgeTypes';
import { TagItem } from './documents/types';
import { PiPlusLight } from 'react-icons/pi';
import Input from '../shared/TailwindComponents/Input';
import SearchInput from '../shared/TailwindComponents/SearchInput';
import Button from '../shared/TailwindComponents/Button';
import Dropdown from '../shared/TailwindComponents/Dropdown';
import { validateStringLength, containsDisallowedCharacters } from '../../utils/validation/validators';
import { FIELD_LABEL_CLASS, FIELD_GAP } from '../shared/TailwindComponents/FieldShell';
import { FieldError } from '../shared/FormComponents';
import Spinner from '../shared/TailwindComponents/Spinner';

const TAG_COLORS: { value: BadgeColor; label: string }[] = [
  { value: 'blue', label: 'Blue' },
  { value: 'green', label: 'Green' },
  { value: 'purple', label: 'Purple' },
  { value: 'orange', label: 'Orange' },
  { value: 'red', label: 'Red' },
  { value: 'cyan', label: 'Cyan' },
  { value: 'indigo', label: 'Indigo' },
  { value: 'pink', label: 'Pink' },
  { value: 'yellow', label: 'Yellow' },
  { value: 'emerald', label: 'Emerald' },
  { value: 'violet', label: 'Violet' },
  { value: 'amber', label: 'Amber' },
];

interface TagPickerModalProps {
  tags: TagItem[];
  fileId?: string;
  folderId?: string;
  onTagsChanged?: (tags: TagItem[]) => void;
  onClose: () => void;
}

const TagPickerModal: React.FC<TagPickerModalProps> = ({
  tags,
  fileId,
  folderId,
  onTagsChanged,
  onClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const [orgTags, setOrgTags] = useState<TagItem[]>([]);
  const [search, setSearch] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [tagNameError, setTagNameError] = useState('');
  const [selectedColor, setSelectedColor] = useState<string>('blue');
  const [currentTags, setCurrentTags] = useState<TagItem[]>(tags);
  const [isLoading, setIsLoading] = useState(true);

  const loadOrgTags = useCallback(async () => {
    setIsLoading(true);
    const result = await dispatch(fetchTags() as any);
    setOrgTags(result || []);
    setIsLoading(false);
  }, [dispatch]);

  useEffect(() => {
    loadOrgTags();
  }, [loadOrgTags]);

  const assignedIds = new Set(currentTags.map(t => t.id));

  const filteredTags = orgTags.filter(t => {
    if (search && !t.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const handleToggleTag = async (tag: TagItem) => {
    const isAssigned = assignedIds.has(tag.id);
    const action = isAssigned ? 'remove' : 'add';
    const result = await dispatch(assignTag(tag.id, action, fileId, folderId) as any);
    setCurrentTags(result || []);
    onTagsChanged?.(result || []);
  };

  const validateTagName = (name: string): string => {
    const trimmed = name.trim();
    if (!trimmed) return '';
    if (containsDisallowedCharacters(trimmed)) {
      return t('Tag name contains invalid characters');
    }
    if (!validateStringLength(trimmed, 1, 50)) {
      return t('Tag name must be between 1 and 50 characters');
    }
    const duplicate = orgTags.find(
      (tag) => tag.name.toLowerCase() === trimmed.toLowerCase()
    );
    if (duplicate) {
      return t('A tag with this name already exists');
    }
    return '';
  };

  const handleTagNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length > 50) return; // Hard cap
    setNewTagName(value);
    if (tagNameError) {
      setTagNameError(validateTagName(value));
    }
  };

  const handleCreateTag = async () => {
    const trimmed = newTagName.trim();
    if (!trimmed) return;
    const error = validateTagName(trimmed);
    if (error) {
      setTagNameError(error);
      return;
    }
    const result = await dispatch(createTag(trimmed, selectedColor) as any);
    if (result?._error === 'duplicate') {
      setTagNameError(t('A tag with this name already exists'));
      return;
    }
    if (result && result.id) {
      setNewTagName('');
      setTagNameError('');
      setIsCreating(false);
      await loadOrgTags();
      // Auto-assign the new tag
      const assignResult = await dispatch(assignTag(result.id, 'add', fileId, folderId) as any);
      setCurrentTags(assignResult || []);
      onTagsChanged?.(assignResult || []);
    }
  };

  return (
    <div className="flex flex-col gap-4 px-4">

      {/* Search */}
      <SearchInput
        value={search}
        onChange={setSearch}
        placeholder={t('Search tags...')}
      />

      {/* Current tags */}
      {currentTags.length > 0 && (
        <div>
          <label className={`${FIELD_LABEL_CLASS} ${FIELD_GAP}`}>
            {t('Assigned')}
          </label>
          <div className="flex flex-wrap gap-1.5 p-2">
            {currentTags.map(tag => (
              <button
                key={tag.id}
                type="button"
                onClick={() => handleToggleTag(tag)}
                className="group"
              >
                <Badge color={(tag.color as BadgeColor) || 'gray'} className="text-xs cursor-pointer">
                  {tag.name}
                  <span className="ms-1 opacity-60 group-hover:opacity-100">&times;</span>
                </Badge>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Available tags */}
      <div>
        <label className={`${FIELD_LABEL_CLASS} ${FIELD_GAP}`}>
          {t('Available')}
        </label>
        {isLoading ? (
          <div className="flex justify-center py-4">
            <Spinner size="glyph" className="!h-4 !w-4" />
          </div>
        ) : (
          <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto p-2">
            {filteredTags.length === 0 && (
              <p className="text-xs text-gray-400 py-2">
                {search ? t('No tags found') : t('No tags yet')}
              </p>
            )}
            {filteredTags.map(tag => {
              const isAssigned = assignedIds.has(tag.id);
              return (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => handleToggleTag(tag)}
                  className="transition-all"
                >
                  <Badge color={(tag.color as BadgeColor) || 'gray'} selected={isAssigned} className="text-xs cursor-pointer">
                    {tag.name}
                  </Badge>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Create new tag */}
      {isCreating ? (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 space-y-2">
          <label className={FIELD_LABEL_CLASS}>
            {t('Create new tag')}
          </label>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Input
                type="text"
                value={newTagName}
                onChange={handleTagNameChange}
                placeholder={t('Tag name')}
                // Boolean: the surface takes the error state, the row below
                // renders the one error message.
                error={!!tagNameError}
                className="w-full text-sm"
                onKeyDown={(e) => e.key === 'Enter' && handleCreateTag()}
                onBlur={() => setTagNameError(validateTagName(newTagName))}
                autoFocus
              />
            </div>
            <div className="w-28">
              <Dropdown
                options={TAG_COLORS}
                value={selectedColor}
                onChange={(v) => setSelectedColor(Array.isArray(v) ? v[0] : v)}
                className="w-full"
                renderOption={(option: any) => (
                  <div className="flex items-center gap-2">
                    <Badge color={option.value as BadgeColor}>{option.label}</Badge>
                  </div>
                )}
              />
            </div>
          </div>
          <FieldError error={tagNameError} />
          <div className="flex items-center gap-2">
            <Button onClick={handleCreateTag} disabled={!newTagName.trim() || !!tagNameError} size="small" variant="primary">
              {t('Create & assign')}
            </Button>
            <Button onClick={() => { setIsCreating(false); setNewTagName(''); setTagNameError(''); }} size="small" variant="neutral">
              {t('Cancel')}
            </Button>
            <span className="ms-auto text-[10px] text-gray-400">{newTagName.trim().length}/50</span>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setIsCreating(true)}
          className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 py-1"
        >
          <PiPlusLight className="w-3.5 h-3.5" />
          {t('Create new tag')}
        </button>
      )}

      {/* Close */}
      <div className="flex justify-end pt-6 border-t border-gray-100 dark:border-gray-700">
        <Button onClick={onClose} size="small" variant="neutral">
          {t('Done')}
        </Button>
      </div>
    </div>
  );
};

export default TagPickerModal;
